import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, Send, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';
import { formatDate } from '@/utils/formatters';

// SUN-50: the Tally Bridge (tally-bridge/voucher-outbox.service.ts) writes these kinds and
// statuses: pending -> claimed (being sent) -> pushed (in Tally) | failed. 'invoice' /
// 'purchase_order' / 'synced' are only on rows from the old Sync Now.
interface SyncRecord {
  id: string;
  entityType: string;
  entityId: string;
  entityNumber: string;
  tallyStatus: string;
  syncedAt: string | null;
  errorMessage: string | null;
  retryCount: number;
}
interface SyncSummary { total: number; synced: number; failed: number; pending: number; sending: number }

const TYPE_LABEL: Record<string, string> = {
  sales_invoice: 'Sales invoice',
  purchase_invoice: 'Purchase invoice',
  credit_note: 'Credit note',
  debit_note: 'Debit note',
  invoice: 'Invoice',
  purchase_order: 'Purchase order',
};
const TYPE_FILTERS = ['sales_invoice', 'purchase_invoice', 'credit_note', 'debit_note'];
const STATUS_FILTERS = ['synced', 'sending', 'pending', 'failed'];

/** Bridge status -> what the page shows. */
export function displayStatus(s: string): 'synced' | 'sending' | 'pending' | 'failed' {
  if (s === 'pushed' || s === 'synced') return 'synced';
  if (s === 'claimed') return 'sending';
  if (s === 'failed') return 'failed';
  return 'pending';
}
const STATUS_LABEL = { synced: 'Synced', sending: 'Sending', pending: 'Pending', failed: 'Failed' } as const;

export function TallySyncPage() {
  const [records, setRecords] = useState<SyncRecord[]>([]);
  const [summary, setSummary] = useState<SyncSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchSyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // SUN-50: filter on the server (the list is the newest 100 rows only) and take the tiles
  // from the server's count of every row.
  const fetchSyncStatus = async () => {
    try {
      const params = !filter ? undefined : STATUS_FILTERS.includes(filter) ? { status: filter } : { entityType: filter };
      const res = await api.get('/integrations/tally/sync-status', params ? { params } : undefined);
      setRecords(res.data.data || []);
      setSummary(res.data.summary ?? null);
    } catch (err) {
      console.error('Failed to fetch tally sync status:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/integrations/tally/sync');
      const d = res.data?.data ?? {};
      const failed: Array<{ number: string; error: string }> = d.failed ?? [];
      if (failed.length) {
        alert(`${d.message}:\n${failed.slice(0, 10).map((f) => `${f.number}: ${f.error}`).join('\n')}`);
      }
      fetchSyncStatus();
    } catch (err: any) {
      const why = String(err?.response?.data?.error || err?.message || 'server error').replace(/\.$/, '');
      alert(`Sync Now failed: ${why}. Check the list below before trying again.`);
    } finally {
      setSyncing(false);
    }
  };

  const retryFailed = async (recordId: string) => {
    try {
      await api.post(`/integrations/tally/retry/${recordId}`);
      fetchSyncStatus();
    } catch (err) {
      alert('Failed to retry');
    }
  };

  const filteredRecords = records;
  const count = (s: string) => records.filter(r => displayStatus(r.tallyStatus) === s).length;
  const stats: SyncSummary = summary ?? {
    total: records.length, synced: count('synced'), failed: count('failed'), pending: count('pending'), sending: count('sending'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tally Sync Status</h1>
          <p className="text-sm text-gray-500">Sync status between AURA and Tally Prime</p>
        </div>
        <div className="flex gap-2">
          <button onClick={triggerSync} disabled={syncing} className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Sync Now
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center" data-testid="tally-tile-total">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center" data-testid="tally-tile-synced">
          <p className="text-2xl font-bold text-green-600">{stats.synced}</p>
          <p className="text-sm text-gray-500">Synced</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center" data-testid="tally-tile-failed">
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          <p className="text-sm text-gray-500">Failed</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center" data-testid="tally-tile-pending">
          <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-center" data-testid="tally-tile-sending">
          <p className="text-2xl font-bold text-blue-600">{stats.sending}</p>
          <p className="text-sm text-gray-500">Sending</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-3">
        <select data-testid="tally-filter" value={filter} onChange={e => setFilter(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="">All Records</option>
          {TYPE_FILTERS.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}s</option>)}
          {STATUS_FILTERS.map(st => <option key={st} value={st}>{STATUS_LABEL[st as keyof typeof STATUS_LABEL]}</option>)}
        </select>
      </div>

      {/* Records Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-500">Entity</th>
              <th className="px-4 py-3 font-medium text-gray-500">Number</th>
              <th className="px-4 py-3 font-medium text-gray-500">Type</th>
              <th className="px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 font-medium text-gray-500">Synced At</th>
              <th className="px-4 py-3 font-medium text-gray-500">Error</th>
              <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRecords.map(record => {
              const st = displayStatus(record.tallyStatus);
              return (
              <tr key={record.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">{TYPE_LABEL[record.entityType] ?? record.entityType}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{record.entityNumber}</td>
                <td className="px-4 py-3 text-gray-600">{record.entityType}</td>
                <td className="px-4 py-3">
                  <span data-testid={`tally-status-${record.id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    st === 'synced' ? 'bg-green-100 text-green-700' :
                    st === 'failed' ? 'bg-red-100 text-red-700' :
                    st === 'sending' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {st === 'synced' && <CheckCircle size={12} />}
                    {st === 'failed' && <XCircle size={12} />}
                    {st === 'pending' && <Clock size={12} />}
                    {st === 'sending' && <Send size={12} />}
                    {STATUS_LABEL[st]}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{record.syncedAt ? formatDate(record.syncedAt) : '-'}</td>
                <td className="px-4 py-3 text-red-600 text-xs">{record.errorMessage || '-'}</td>
                <td className="px-4 py-3">
                  {record.tallyStatus === 'failed' && record.retryCount < 3 && (
                    <button onClick={() => retryFailed(record.id)} className="text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]">
                      Retry
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}